import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import LiveMeasurementCapture from '../../components/LiveMeasurementCapture';
import ConstructionContextSelector from '../../components/ConstructionContextSelector';
import LogQuantityEvidence from '../../components/LogQuantityEvidence';
import LinkActivityModal from '../../components/LinkActivityModal';
import { useOffline } from '../../contexts/OfflineContext';
import { useTranslation } from 'react-i18next';
import { Upload, Save, Trash2, Loader2, FileText, Download, Camera, Undo2, Redo2 } from 'lucide-react';
import { toast } from 'sonner';
import { shareOrDownloadPdf } from '../../lib/native/share';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Measure2D = () => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const { queueCapture } = useOffline();
  const [project, setProject] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [mode, setMode] = useState('reference');  // reference, polygon
  const [referencePoints, setReferencePoints] = useState([]);
  const [referenceLength, setReferenceLength] = useState('');
  const [expectedArea, setExpectedArea] = useState('');
  const [unit, setUnit] = useState('meters');
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [hoverPoint, setHoverPoint] = useState(null);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [measurements, setMeasurements] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [constructionCtx, setConstructionCtx] = useState({});
  const [showLogEvidence, setShowLogEvidence] = useState(false);
  const [linkActivity, setLinkActivity] = useState(null); // {jobId, ctx} | null
  
  // Live camera measurement state
  const [inputMode, setInputMode] = useState('upload'); // 'upload' | 'camera'
  const [showCameraGuide, setShowCameraGuide] = useState(false);
  const [liveCameraEnabled, setLiveCameraEnabled] = useState(false);
  const [downloadingSession, setDownloadingSession] = useState(null);
  
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const draggingIndexRef = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    fetchProject();
    fetchUploads();
    fetchJobs();
    fetchFeatureFlags();
  }, [projectId]);

  useEffect(() => {
    if (selectedUpload && imageLoaded && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      if (canvas.width !== imageRef.current.width || canvas.height !== imageRef.current.height) {
        canvas.width = imageRef.current.width;
        canvas.height = imageRef.current.height;
      }
      drawCanvas();
    }
  }, [selectedUpload, imageLoaded, referencePoints, polygonPoints, mode, hoverPoint, snapEnabled]);

  // Undo/Redo the last outline point via Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z (or Ctrl+Y).
  useEffect(() => {
    if (!imageLoaded) return;
    const onKey = (e) => {
      const el = document.activeElement;
      const tag = el && el.tagName ? el.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || (el && el.isContentEditable)) return; // don't hijack native undo while typing
      const key = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === 'z' && !e.shiftKey) { e.preventDefault(); undoPolygonPoint(); }
      else if ((e.ctrlKey || e.metaKey) && ((key === 'z' && e.shiftKey) || key === 'y')) { e.preventDefault(); redoPolygonPoint(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [imageLoaded, polygonPoints, redoStack]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error(t('oi.toastFetchProjectFail'));
    }
  };

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/uploads`);
      setUploads(response.data.filter(u => u.mimeType.startsWith('image/')));
    } catch (error) {
      console.error('Failed to fetch uploads');
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const response = await axios.get(`${API}/config/features`);
      setLiveCameraEnabled(!!response.data.liveCameraMeasure);
    } catch (error) {
      setLiveCameraEnabled(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/analysis-jobs?type=MEASURE_2D`);
      setJobs([...response.data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    } catch (error) {
      console.error('Failed to fetch jobs');
    }
  };

  // Download a branded PDF summarising an entire multi-room live-camera session.
  const downloadSessionReport = async (sessionId) => {
    setDownloadingSession(sessionId);
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/measure/live/session/${sessionId}/report`,
        {},
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      // Native: OS share sheet; Web: exact existing anchor-download (unchanged).
      await shareOrDownloadPdf(blob, `session_report_${sessionId.slice(0, 8)}.pdf`, 'ReasonVisionX Session Report');
      toast.success(t('measure.sessionReportReady', { defaultValue: 'Session report downloaded' }));
    } catch (error) {
      toast.error(t('measure.sessionReportFail', { defaultValue: 'Failed to generate session report' }));
    } finally {
      setDownloadingSession(null);
    }
  };
  
  // Handler for live camera measurement completion (synchronous, multi-room session)
  const handleCameraComplete = async (captureData) => {
    setShowCameraGuide(false);
    setInputMode('upload');
    setSaving(true);

    const sessionPayload = {
      rooms: captureData.rooms,
      sessionName: captureData.sessionName || null,
      constructionContext: Object.keys(constructionCtx || {}).length ? constructionCtx : null,
    };

    // Phase 9: offline capture — queue the live-measure session for later sync.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      try {
        await queueCapture({
          type: 'live-measure',
          projectId,
          projectName: project?.name,
          label: `Live measure · ${captureData.rooms?.length || 0} room(s)`,
          payload: sessionPayload,
        });
        toast.success(t('measure.toastSavedOffline'));
      } catch (e) {
        toast.error(t('measure.toastOfflineFail'));
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      const response = await axios.post(`${API}/projects/${projectId}/measure/live/session`, sessionPayload);

      const data = response.data;
      const firstRoom = (data.rooms && data.rooms[0]) || null;
      if (firstRoom) {
        const r = firstRoom.result || {};
        setPolygonPoints(r.polygonPoints || []);
        setReferenceLength(r.referenceLength?.toString() || '');
        setMeasurements({ area: r.area, perimeter: r.perimeter, unit: r.unit || 'meters' });
      }

      if (data.roomCount > 1) {
        toast.success(t('measure.toastSessionSaved', { count: data.roomCount, area: data.combinedArea, unit: data.unit }));
      } else {
        toast.success(t('measure.toastLiveSaved'));
      }
      fetchJobs();
    } catch (error) {
      console.error('Live measurement failed:', error);
      toast.error(error.response?.data?.detail || t('measure.toastLiveFail'));
    } finally {
      setSaving(false);
    }
  };

  const loadImage = async (uploadId) => {
    try {
      const response = await axios.get(`${API}/uploads/${uploadId}/signed-url`);
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        imageRef.current = img;
        setImageLoaded(true);
        // Canvas mounts only after imageLoaded flips true — draw once it's in the DOM.
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = img.width;
          canvas.height = img.height;
          drawCanvas();
        }
      };
      img.src = response.data.url;
    } catch (error) {
      toast.error(t('measure.toastLoadImageFail'));
    }
  };

  const drawCanvas = () => {
    if (!canvasRef.current || !imageRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imageRef.current, 0, 0);
    
    // Draw reference line
    if (referencePoints.length === 2) {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(referencePoints[0].x, referencePoints[0].y);
      ctx.lineTo(referencePoints[1].x, referencePoints[1].y);
      ctx.stroke();
      
      // Draw endpoints
      referencePoints.forEach(p => {
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 6, 0, 2 * Math.PI);
        ctx.fill();
      });
    }
    
    // Draw polygon
    if (polygonPoints.length > 0) {
      ctx.strokeStyle = '#3b82f6';
      ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
      ctx.lineWidth = 2;
      
      ctx.beginPath();
      ctx.moveTo(polygonPoints[0].x, polygonPoints[0].y);
      for (let i = 1; i < polygonPoints.length; i++) {
        ctx.lineTo(polygonPoints[i].x, polygonPoints[i].y);
      }
      if (polygonPoints.length > 2) {
        ctx.closePath();
        ctx.fill();
      }
      ctx.stroke();
      
      // Draw vertices
      polygonPoints.forEach((p, i) => {
        ctx.fillStyle = '#3b82f6';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 5, 0, 2 * Math.PI);
        ctx.fill();
        
        // Draw index
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Arial';
        ctx.fillText(i + 1, p.x - 4, p.y + 4);
      });
    }

    // Snap-to-edge alignment guides + live preview (manual assist only, no auto-detection)
    if (mode === 'polygon' && hoverPoint) {
      const { point: sp, guides } = getSnapped(hoverPoint);
      ctx.save();
      // alignment guide lines (dashed green) when the hover aligns with an existing vertex
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.85)';
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 6]);
      guides.forEach((g) => {
        if (g.kind === 'ray') {
          const L = Math.hypot(canvas.width, canvas.height);
          ctx.beginPath();
          ctx.moveTo(g.from.x - L * Math.cos(g.angle), g.from.y - L * Math.sin(g.angle));
          ctx.lineTo(g.from.x + L * Math.cos(g.angle), g.from.y + L * Math.sin(g.angle));
          ctx.stroke();
        } else if (g.kind === 'close') {
          ctx.beginPath();
          ctx.arc(g.at.x, g.at.y, 10, 0, 2 * Math.PI);
          ctx.stroke();
        }
      });
      ctx.setLineDash([]);
      // preview segment from the last placed vertex to the (snapped) cursor
      if (polygonPoints.length > 0) {
        const last = polygonPoints[polygonPoints.length - 1];
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(sp.x, sp.y);
        ctx.stroke();
      }
      // preview vertex — green when snapping to a guide, blue otherwise
      ctx.fillStyle = guides.length ? '#10b981' : '#3b82f6';
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();
    }
  };

  // Returns the cursor point snapped to clean 45°/90° wall angles from the previous vertex
  // (and locked onto the first vertex when closing the loop), plus active guide lines.
  const ANGLE_SNAP_RAD = (8 * Math.PI) / 180; // tolerance around each 45° increment
  // canvas-internal → display-pixel scale (canvas pixels can differ wildly from CSS size).
  const getScale = () => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { sx: canvasRef.current.width / rect.width, sy: canvasRef.current.height / rect.height };
  };
  const getSnapped = (pt) => {
    const guides = [];
    if (!snapEnabled || mode !== 'polygon' || !canvasRef.current || polygonPoints.length === 0) {
      return { point: pt, guides };
    }
    const last = polygonPoints[polygonPoints.length - 1];

    // 1) Snap-to-close: near the first vertex (within ~18 display px), lock exactly onto it.
    if (polygonPoints.length >= 3) {
      const first = polygonPoints[0];
      const { sx, sy } = getScale();
      if (Math.hypot((pt.x - first.x) / sx, (pt.y - first.y) / sy) <= 18) {
        guides.push({ kind: 'close', at: first });
        return { point: { x: first.x, y: first.y }, guides };
      }
    }

    // 2) Angle snap: lock the wall direction to the nearest 45° increment from the previous point.
    const dx = pt.x - last.x;
    const dy = pt.y - last.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 1) {
      const step = Math.PI / 4; // 45°
      const angle = Math.atan2(dy, dx);
      const snapAngle = Math.round(angle / step) * step;
      let diff = Math.abs(angle - snapAngle);
      diff = Math.min(diff, 2 * Math.PI - diff);
      if (diff <= ANGLE_SNAP_RAD) {
        const x = last.x + dist * Math.cos(snapAngle);
        const y = last.y + dist * Math.sin(snapAngle);
        guides.push({ kind: 'ray', from: last, angle: snapAngle });
        return { point: { x, y }, guides };
      }
    }
    return { point: pt, guides };
  };

  const canvasPointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  // Index of an existing outline vertex under the cursor (~14 display px), or -1.
  const findVertexAt = (pt) => {
    if (!canvasRef.current || mode !== 'polygon') return -1;
    const { sx, sy } = getScale();
    for (let i = polygonPoints.length - 1; i >= 0; i--) {
      if (Math.hypot((pt.x - polygonPoints[i].x) / sx, (pt.y - polygonPoints[i].y) / sy) <= 14) return i;
    }
    return -1;
  };

  const handleCanvasDown = (e) => {
    if (mode !== 'polygon' || !canvasRef.current) return;
    const idx = findVertexAt(canvasPointFromEvent(e));
    if (idx !== -1) {
      draggingIndexRef.current = idx;      // begin dragging an existing point
      suppressClickRef.current = true;     // don't let the trailing click add a new point
      setHoverPoint(null);
    }
  };

  const handleCanvasMove = (e) => {
    if (mode !== 'polygon' || !canvasRef.current) return;
    const pt = canvasPointFromEvent(e);
    if (draggingIndexRef.current !== null) {
      const idx = draggingIndexRef.current;
      setPolygonPoints((pts) => pts.map((p, i) => (i === idx ? pt : p)));
      setMeasurements(null); // geometry changed — force a recalculate
      return;
    }
    setHoverPoint(pt);
  };

  const handleCanvasUp = () => {
    draggingIndexRef.current = null;
  };

  const handleCanvasLeave = () => {
    draggingIndexRef.current = null;
    setHoverPoint(null);
  };

  const undoPolygonPoint = () => {
    if (polygonPoints.length === 0) return;
    const last = polygonPoints[polygonPoints.length - 1];
    setPolygonPoints(polygonPoints.slice(0, -1));
    setRedoStack((r) => [...r, last]);
    setMeasurements(null);
  };

  const redoPolygonPoint = () => {
    if (redoStack.length === 0) return;
    const p = redoStack[redoStack.length - 1];
    setRedoStack(redoStack.slice(0, -1));
    setPolygonPoints((pts) => [...pts, p]);
    setMeasurements(null);
  };

  const handleCanvasClick = (e) => {
    if (!canvasRef.current) return;
    if (suppressClickRef.current) { suppressClickRef.current = false; return; } // was a drag/vertex grab, not a new point
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Scale to actual canvas coordinates
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    const actualX = x * scaleX;
    const actualY = y * scaleY;
    
    if (mode === 'reference') {
      if (referencePoints.length < 2) {
        setReferencePoints([...referencePoints, { x: actualX, y: actualY }]);
      }
    } else if (mode === 'polygon') {
      const { point } = getSnapped({ x: actualX, y: actualY });
      setPolygonPoints([...polygonPoints, point]);
      setRedoStack([]);
    }
  };

  const clearReference = () => {
    setReferencePoints([]);
    setReferenceLength('');
  };

  const clearPolygon = () => {
    setPolygonPoints([]);
    setMeasurements(null);
    setRedoStack([]);
    setHoverPoint(null);
  };

  const calculate = () => {
    if (referencePoints.length !== 2) {
      toast.error(t('measure.toastDrawRefFirst'));
      return;
    }
    if (!referenceLength || parseFloat(referenceLength) <= 0) {
      toast.error(t('measure.toastValidRefLength'));
      return;
    }
    if (polygonPoints.length < 3) {
      toast.error(t('measure.toastMin3Points'));
      return;
    }
    
    // Simple local calculation for preview
    const refDist = Math.sqrt(
      Math.pow(referencePoints[1].x - referencePoints[0].x, 2) +
      Math.pow(referencePoints[1].y - referencePoints[0].y, 2)
    );
    const ratio = parseFloat(referenceLength) / refDist;
    
    // Calculate area using Shoelace formula
    let area = 0;
    const n = polygonPoints.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += polygonPoints[i].x * polygonPoints[j].y;
      area -= polygonPoints[j].x * polygonPoints[i].y;
    }
    area = Math.abs(area) / 2;
    area = area * (ratio * ratio);
    
    // Calculate perimeter
    let perimeter = 0;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const dist = Math.sqrt(
        Math.pow(polygonPoints[j].x - polygonPoints[i].x, 2) +
        Math.pow(polygonPoints[j].y - polygonPoints[i].y, 2)
      );
      perimeter += dist;
    }
    perimeter = perimeter * ratio;
    
    setMeasurements({ area: area.toFixed(2), perimeter: perimeter.toFixed(2) });
  };

  const saveMeasurement = async () => {
    if (!measurements) {
      toast.error(t('measure.toastCalcFirst'));
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        uploadId: selectedUpload.id,
        referenceLinePixels: {
          x1: referencePoints[0].x,
          y1: referencePoints[0].y,
          x2: referencePoints[1].x,
          y2: referencePoints[1].y
        },
        referenceLength: parseFloat(referenceLength),
        unit: unit,
        polygonPoints: polygonPoints,
        expectedArea: expectedArea ? parseFloat(expectedArea) : null,
        constructionContext: Object.keys(constructionCtx || {}).length ? constructionCtx : null,
      };
      
      await axios.post(`${API}/projects/${projectId}/measure/2d/run`, payload);
      toast.success(t('measure.toastSaved'));
      fetchJobs();
      clearPolygon();
      clearReference();
    } catch (error) {
      toast.error(t('measure.toastSaveFail'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('measure.title')}</h1>
          <p className="text-slate-600 mt-1">{project?.name}</p>
        </div>

        {/* Phase 1: Construction context — link this measurement to a physical location */}
        <ConstructionContextSelector projectId={projectId} value={constructionCtx} onChange={setConstructionCtx} />

        {/* Input Mode Selection (camera option is feature-flagged) */}
        {liveCameraEnabled && (
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('measure.selectInputMethod')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => { setInputMode('upload'); setShowCameraGuide(false); }}
              className={`p-6 rounded-lg border-2 transition-all ${
                inputMode === 'upload'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-blue-300'
              }`}
              data-testid="mode-upload"
            >
              <Upload className="w-8 h-8 mx-auto mb-3 text-blue-600" />
              <h3 className="font-semibold text-lg mb-2">{t('measure.uploadImage')}</h3>
              <p className="text-sm text-slate-600">{t('measure.uploadImageDesc')}</p>
            </button>
            
            <button
              onClick={() => { setInputMode('camera'); setShowCameraGuide(true); }}
              className={`p-6 rounded-lg border-2 transition-all ${
                inputMode === 'camera'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-green-300'
              }`}
              data-testid="mode-camera"
            >
              <Camera className="w-8 h-8 mx-auto mb-3 text-green-600" />
              <h3 className="font-semibold text-lg mb-2">{t('measure.measureCamera')}</h3>
              <p className="text-sm text-slate-600">{t('measure.measureCameraDesc')}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                {t('measure.live')} ✨
              </span>
            </button>
          </div>
        </div>
        )}

        {/* Live Camera Measurement (full-screen) */}
        {showCameraGuide && inputMode === 'camera' && liveCameraEnabled && (
          <LiveMeasurementCapture
            onComplete={handleCameraComplete}
            onCancel={() => {
              setShowCameraGuide(false);
              setInputMode('upload');
            }}
          />
        )}

        {/* Existing Upload-Based Workflow (unchanged) */}
        {inputMode === 'upload' && !showCameraGuide && (
          <>
            {/* Image Selection */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('measure.step1')}</h2>
              <select value={selectedUpload?.id || ''} onChange={(e) => {
            const upload = uploads.find(u => u.id === e.target.value);
            setSelectedUpload(upload);
            if (upload) {
              loadImage(upload.id);
              clearReference();
              clearPolygon();
            }
          }} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg" data-testid="upload-select">
            <option value="">{t('measure.selectImage')}</option>
            {uploads.map((upload) => (
              <option key={upload.id} value={upload.id}>{upload.fileName}</option>
            ))}
          </select>
        </div>

        {imageLoaded && (
          <>
            {/* Discoverable step guide (no logic change; guides the manual flow) */}
            {(() => {
              const hasScale = referencePoints.length === 2 && referenceLength && parseFloat(referenceLength) > 0;
              const hasArea = polygonPoints.length >= 3;
              const current = measurements ? 4 : hasArea ? 3 : hasScale ? 2 : 1;
              const steps = [
                { n: 1, label: t('measure.stepScale', { defaultValue: 'Set Scale' }) },
                { n: 2, label: t('measure.stepArea', { defaultValue: 'Draw Measurement Area' }) },
                { n: 3, label: t('measure.stepCalc', { defaultValue: 'Calculate' }) },
                { n: 4, label: t('measure.stepReview', { defaultValue: 'Review & Save' }) },
              ];
              const instructions = {
                1: t('measure.instrScale', { defaultValue: 'Set a known reference length first so ReasonVisionX can convert image pixels into real-world measurements. Click “Draw Reference Line”, mark two points across a known distance, then enter its real size and unit.' }),
                2: t('measure.instrArea', { defaultValue: 'Now trace the shape you want to measure. Click “Draw Area”, then click around the outline (at least 3 points). Drag any point to adjust; use Undo if needed.' }),
                3: t('measure.instrCalc', { defaultValue: 'Your outline is ready. Click “Calculate” to compute the area and perimeter from your calibrated scale.' }),
                4: t('measure.instrReview', { defaultValue: 'Review the calculated area and perimeter, then Save the measurement. Use “Start Again” to remeasure.' }),
              };
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="measure-step-guide">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
                      {steps.map((s) => (
                        <div key={s.n} className={`flex items-center gap-2 ${current === s.n ? 'font-bold text-blue-800' : current > s.n ? 'text-green-700' : 'text-slate-500'}`} data-testid={`guide-step-${s.n}`}>
                          <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs ${current > s.n ? 'bg-green-600 text-white' : current === s.n ? 'bg-blue-600 text-white ring-2 ring-blue-300' : 'bg-slate-300 text-slate-600'}`}>{current > s.n ? '✓' : s.n}</span>
                          <span className="text-sm whitespace-nowrap">{s.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="px-2 py-1 bg-white border border-slate-200 rounded-full text-xs text-slate-600" data-testid="measure-unit-chip">
                        {t('measure.unit', { defaultValue: 'Unit' })}: {unit === 'feet' ? t('measure.feet', { defaultValue: 'Feet' }) : t('measure.meters', { defaultValue: 'Meters' })}
                      </span>
                      {(referencePoints.length > 0 || polygonPoints.length > 0 || measurements) && (
                        <button
                          onClick={() => { clearPolygon(); clearReference(); setMode('reference'); }}
                          className="px-3 py-1 bg-slate-200 text-slate-700 rounded-full text-xs hover:bg-slate-300"
                          data-testid="measure-start-again-btn"
                        >
                          {t('measure.startAgain', { defaultValue: 'Start Again' })}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="bg-white/70 border border-blue-100 rounded-lg p-3" data-testid="measure-current-instruction">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">{t('measure.stepLabel', { defaultValue: 'Step' })} {current}:</span> {instructions[current]}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 mt-2" data-testid="measure-help-text">
                    {t('measure.helpText', { defaultValue: 'This is precise, user-calibrated measurement — you mark a known length, then trace the outline. No automatic detection is used, so accuracy depends on your points.' })}
                  </p>
                </div>
              );
            })()}
            {/* Reference Scale */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('measure.step2')}</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 mb-2">{t('measure.refHint')}</p>
                  <button onClick={() => setMode('reference')} className={`px-4 py-2 rounded-lg ${mode === 'reference' ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                    {t('measure.drawRefLine')} {referencePoints.length > 0 && `(${referencePoints.length}/2)`}
                  </button>
                  {referencePoints.length > 0 && (
                    <button onClick={clearReference} className="ml-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">
                      <Trash2 className="w-4 h-4 inline mr-1" /> {t('measure.clear')}
                    </button>
                  )}
                </div>
                {referencePoints.length === 2 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t('measure.realLength')}</label>
                      <input type="number" step="0.01" value={referenceLength} onChange={(e) => setReferenceLength(e.target.value)} placeholder={t('measure.enterLength')} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg" data-testid="reference-length-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">{t('measure.unit')}</label>
                      <select value={unit} onChange={(e) => setUnit(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg" data-testid="unit-select">
                        <option value="meters">{t('measure.meters')}</option>
                        <option value="feet">{t('measure.feet')}</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Guidance when the outline step isn't unlocked yet */}
            {!(referencePoints.length === 2 && referenceLength) && (
              <div className="bg-white rounded-xl p-4 border border-dashed border-slate-300 text-sm text-slate-500" data-testid="polygon-locked-hint">
                {t('measure.polygonLocked', { defaultValue: 'Next: mark the room outline. First finish step 1–2 above — mark a reference length and enter its real size — then the outline step unlocks.' })}
              </div>
            )}

            {/* Polygon Drawing */}
            {referencePoints.length === 2 && referenceLength && (
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('measure.step3')}</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-2">{t('measure.polygonHint')}</p>
                    <button onClick={() => setMode('polygon')} className={`px-4 py-2 rounded-lg ${mode === 'polygon' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-700'}`}>
                      {t('measure.drawPolygon')} {polygonPoints.length > 0 && `(${polygonPoints.length} ${t('measure.points')})`}
                    </button>
                    {polygonPoints.length > 0 && (
                      <button onClick={clearPolygon} className="ml-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">
                        <Trash2 className="w-4 h-4 inline mr-1" /> {t('measure.clear')}
                      </button>
                    )}
                    <button
                      onClick={undoPolygonPoint}
                      disabled={polygonPoints.length === 0}
                      title={t('measure.undo', { defaultValue: 'Undo last point (Ctrl+Z)' })}
                      className="ml-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="undo-point-button"
                    >
                      <Undo2 className="w-4 h-4 inline mr-1" /> {t('measure.undo', { defaultValue: 'Undo' })}
                    </button>
                    <button
                      onClick={redoPolygonPoint}
                      disabled={redoStack.length === 0}
                      title={t('measure.redo', { defaultValue: 'Redo last point (Ctrl+Shift+Z)' })}
                      className="ml-2 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="redo-point-button"
                    >
                      <Redo2 className="w-4 h-4 inline mr-1" /> {t('measure.redo', { defaultValue: 'Redo' })}
                    </button>
                    <button
                      onClick={calculate}
                      disabled={polygonPoints.length < 3}
                      title={polygonPoints.length < 3 ? t('measure.needThreePoints', { defaultValue: 'Mark at least 3 points on the room outline' }) : ''}
                      className={`ml-2 px-6 py-3 rounded-lg font-bold text-base transition-colors ${polygonPoints.length >= 3 ? 'bg-green-600 text-white hover:bg-green-700 shadow-md' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                      data-testid="calculate-button"
                    >
                      {t('measure.calculate')} {polygonPoints.length < 3 ? `(${polygonPoints.length}/3)` : '→'}
                    </button>
                    {polygonPoints.length > 0 && polygonPoints.length < 3 && (
                      <p className="text-xs text-amber-600 mt-2" data-testid="calc-hint">
                        {t('measure.needThreePoints', { defaultValue: 'Mark at least 3 points on the room outline to calculate.' })}
                      </p>
                    )}
                    <label className="flex items-center gap-2 mt-3 text-sm text-slate-600 cursor-pointer select-none" data-testid="snap-toggle-label">
                      <input type="checkbox" checked={snapEnabled} onChange={(e) => setSnapEnabled(e.target.checked)} className="w-4 h-4 accent-emerald-600" data-testid="snap-toggle" />
                      {t('measure.snapGuides', { defaultValue: 'Snap to 45°/90° wall angles' })}
                    </label>
                    <p className="text-xs text-slate-400 mt-1" data-testid="drag-hint">
                      {t('measure.dragHint', { defaultValue: 'Tip: drag any placed point to nudge it. Undo with Ctrl+Z.' })}
                    </p>
                  </div>
                  {measurements && (
                    <div className="grid grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div>
                        <p className="text-sm text-slate-600">{t('measure.area')}</p>
                        <p className="text-2xl font-bold text-slate-900">{measurements.area} {unit}²</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">{t('measure.perimeter')}</p>
                        <p className="text-2xl font-bold text-slate-900">{measurements.perimeter} {unit}</p>
                      </div>
                    </div>
                  )}
                  {/* Phase 4: optional expected area for deviation flagging */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('measure.expectedAreaLabel')}</label>
                    <input type="number" step="0.01" value={expectedArea} onChange={(e) => setExpectedArea(e.target.value)} placeholder={t('measure.plannedAreaPlaceholder', { unit })} className="w-full sm:w-64 px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg" data-testid="expected-area-input" />
                    {measurements && expectedArea && parseFloat(expectedArea) > 0 && (() => {
                      const pct = ((measurements.area - parseFloat(expectedArea)) / parseFloat(expectedArea)) * 100;
                      return (
                        <span className={`ml-0 sm:ml-3 mt-2 sm:mt-0 inline-block px-3 py-1 rounded-full text-sm font-semibold ${Math.abs(pct) > 5 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid="measure-deviation-preview">
                          {pct > 0 ? '+' : ''}{pct.toFixed(1)}{t('measure.vsPlanned')}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Canvas */}
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <canvas ref={canvasRef} onClick={handleCanvasClick} onMouseDown={handleCanvasDown} onMouseMove={handleCanvasMove} onMouseUp={handleCanvasUp} onMouseLeave={handleCanvasLeave} className="w-full border-2 border-slate-300 rounded-lg cursor-crosshair" style={{ maxHeight: '600px', objectFit: 'contain' }} />
            </div>

            {/* Save */}
            {measurements && (
              <button onClick={saveMeasurement} disabled={saving} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 shadow-md" data-testid="save-measurement-btn">
                {saving ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{t('common.saving')}</span></> : <><Save className="w-5 h-5" /><span>{t('measure.saveMeasurement')}</span></>}
              </button>
            )}
            {measurements && (
              <button onClick={() => setShowLogEvidence(true)} className="w-full mt-2 flex items-center justify-center gap-2 bg-slate-800 text-white py-3 rounded-lg hover:bg-slate-900" data-testid="log-evidence-btn">
                Log as BOQ Evidence
              </button>
            )}
          </>
        )}
          </>
        )}

        {/* Live Camera Sessions — grouped multi-room summaries with PDF export */}
        {(() => {
          const groups = {};
          jobs.forEach((j) => {
            if (j.sessionId) {
              (groups[j.sessionId] = groups[j.sessionId] || []).push(j);
            }
          });
          const sessionGroups = Object.entries(groups)
            .map(([sid, js]) => ({
              sessionId: sid,
              jobs: js,
              combinedArea: js.reduce((s, j) => s + (j.result?.area || 0), 0),
              unit: js[0]?.result?.unit || 'meters',
              createdAt: js.map((j) => j.createdAt).sort()[0],
            }))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          if (sessionGroups.length === 0) return null;
          return (
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm" data-testid="live-sessions-card">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                {t('measure.liveSessions', { defaultValue: 'Live Camera Sessions' })}
              </h2>
              <div className="space-y-3">
                {sessionGroups.map((g) => (
                  <div
                    key={g.sessionId}
                    className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200"
                    data-testid={`session-group-${g.sessionId.slice(0, 8)}`}
                  >
                    <div>
                      <p className="font-semibold text-slate-900">
                        {g.jobs.length} {t('measure.roomsLabel', { defaultValue: 'rooms' })} ·{' '}
                        {t('measure.totalArea', { defaultValue: 'Total' })} {g.combinedArea.toFixed(2)} {g.unit}²
                      </p>
                      <p className="text-sm text-slate-600">{new Date(g.createdAt).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                    <button
                      onClick={() => setLinkActivity({ jobId: g.jobs[0]?.id, ctx: g.jobs[0]?.constructionContext })}
                      className="flex items-center gap-2 bg-sky-700 text-white px-3 py-2 rounded-lg hover:bg-sky-800 text-sm font-medium"
                      data-testid={`link-activity-session-${g.sessionId.slice(0, 8)}`}
                    >
                      <Download className="w-4 h-4" /> Link to Activity
                    </button>
                    <button
                      onClick={() => downloadSessionReport(g.sessionId)}
                      disabled={downloadingSession === g.sessionId}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                      data-testid={`download-session-report-${g.sessionId.slice(0, 8)}`}
                    >
                      {downloadingSession === g.sessionId ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {t('common.saving', { defaultValue: 'Working…' })}</>
                      ) : (
                        <><Download className="w-4 h-4" /> {t('measure.sessionReport', { defaultValue: 'Session PDF' })}</>
                      )}
                    </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Previous Measurements */}
        {jobs.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('measure.previousMeasurements')}</h2>
            <div className="space-y-2">
              {jobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <p className="font-medium text-slate-900">
                      {(job.roomName || job.result?.roomName) && (
                        <span className="text-slate-500 mr-1">{job.roomName || job.result?.roomName}:</span>
                      )}
                      Area: {job.result?.area} {job.result?.unit}²
                    </p>
                    <p className="text-sm text-slate-600">
                      {new Date(job.createdAt).toLocaleString()}
                      {job.sessionId && <span className="ml-2 text-amber-600">· {t('measure.session')}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setLinkActivity({ jobId: job.id, ctx: job.constructionContext })} className="px-3 py-1 bg-sky-700 text-white rounded-full text-xs hover:bg-sky-800" data-testid="link-activity-btn">Link to Activity</button>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${(job.captureSource === 'camera_live') ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {job.captureSource === 'camera_live' ? t('measure.liveCamera') : t('measure.upload')}
                    </span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">{job.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {showLogEvidence && measurements && (
        <LogQuantityEvidence projectId={projectId} sourceType="MEASUREMENT_2D" sourceId={null}
          observationType="MEASUREMENT" measurementType="2D" unit="M2"
          aiQuantity={measurements.area ? Number(measurements.area) : null}
          constructionContext={constructionCtx} onClose={() => setShowLogEvidence(false)} onLogged={() => {}} />
      )}
      {linkActivity?.jobId && (
        <LinkActivityModal projectId={projectId} evidenceType="job" evidenceId={linkActivity.jobId}
          constructionContext={linkActivity.ctx} onClose={() => setLinkActivity(null)} onLinked={() => {}} />
      )}
    </Layout>
  );
};

export default Measure2D;
